import { memo } from 'react';
import { LuPencil, LuRefreshCw } from 'react-icons/lu';
import AreaSelectionMethod, { AREA_METHODS } from './AreaSelectionMethod';
import {
	SidebarSection,
	SectionTitle,
	SectionContent,
	ActionButton,
	PolygonInfo,
} from './FlightPlannerStyles';

/**
 * Step 1 — Define Flight Area panel.
 * Supports three methods: draw polygon, select UTM grid squares, or import file.
 */
const WorkflowDrawing = ({
	areaMethod,
	onAreaMethodChange,
	utmGridSize,
	onUtmGridSizeChange,
	onGridSearch,
	importedFileName,
	onFileImport,
	onClearImport,
	polygonPoints,
	isDrawingActive,
	onStartDrawing,
	selectedGridSquares,
	onClearGridSelection,
	formattedArea,
}) => (
	<>
		<SidebarSection>
			<SectionTitle>Define Flight Area</SectionTitle>
			<SectionContent>
				<AreaSelectionMethod
					selectedMethod={areaMethod}
					onMethodChange={onAreaMethodChange}
					onFileImport={onFileImport}
					gridSize={utmGridSize}
					onGridSizeChange={onUtmGridSizeChange}
					onGridSearch={onGridSearch}
					importedFileName={importedFileName}
					onClearImport={onClearImport}
				/>
			</SectionContent>
		</SidebarSection>

		{areaMethod === AREA_METHODS.DRAW && (
			<SidebarSection>
				<SectionTitle>Draw Polygon</SectionTitle>
				<SectionContent>
					{!isDrawingActive && polygonPoints.length === 0 && (
						<>
							<p>Click the button below, then click on the map to place vertices.</p>
							<ActionButton
								variant="primary"
								onClick={onStartDrawing}
								style={{ marginTop: '0.5rem', marginBottom: '0.5rem' }}>
								<LuPencil
									size={14}
									style={{ verticalAlign: 'middle', marginRight: '4px' }}
								/>{' '}
								Start Drawing
							</ActionButton>
						</>
					)}

					{isDrawingActive && (
						<>
							<p>Drawing mode active:</p>
							<ul style={{ paddingLeft: '1.25rem', marginTop: '0.75rem' }}>
								<li>Click to place vertices</li>
								<li>Double-click to complete</li>
								<li>Press Esc to cancel</li>
							</ul>
						</>
					)}

					{!isDrawingActive && polygonPoints.length >= 3 && (
						<>
							<p>Polygon complete. You can drag vertices to adjust, or redraw.</p>
							<PolygonInfo>
								<div className="info-row">
									<span className="label">Vertices:</span>
									<span className="value">{polygonPoints.length}</span>
								</div>
								<div className="info-row">
									<span className="label">Area:</span>
									<span className="value">{formattedArea}</span>
								</div>
							</PolygonInfo>
							<ActionButton
								variant="outline-secondary"
								onClick={onStartDrawing}
								style={{ marginTop: '0.75rem' }}>
								<LuRefreshCw
									size={14}
									style={{ verticalAlign: 'middle', marginRight: '4px' }}
								/>{' '}
								Redraw Polygon
							</ActionButton>
						</>
					)}
				</SectionContent>
			</SidebarSection>
		)}

		{areaMethod === AREA_METHODS.UTM_GRID && selectedGridSquares.length > 0 && (
			<SidebarSection>
				<SectionTitle>Selected Grid</SectionTitle>
				<SectionContent>
					<PolygonInfo>
						<div className="info-row">
							<span className="label">Squares:</span>
							<span className="value">{selectedGridSquares.length}</span>
						</div>
						<div className="info-row">
							<span className="label">Area:</span>
							<span className="value">{formattedArea}</span>
						</div>
					</PolygonInfo>
					<ActionButton
						variant="outline-secondary"
						onClick={onClearGridSelection}
						style={{ marginTop: '0.75rem' }}>
						<LuRefreshCw
							size={14}
							style={{ verticalAlign: 'middle', marginRight: '4px' }}
						/>{' '}
						Clear Selection
					</ActionButton>
				</SectionContent>
			</SidebarSection>
		)}

		{areaMethod === AREA_METHODS.IMPORT_FILE &&
			importedFileName &&
			polygonPoints.length >= 3 && (
				<SidebarSection>
					<SectionTitle>Imported Area</SectionTitle>
					<SectionContent>
						<PolygonInfo>
							<div className="info-row">
								<span className="label">Vertices:</span>
								<span className="value">{polygonPoints.length}</span>
							</div>
							<div className="info-row">
								<span className="label">Area:</span>
								<span className="value">{formattedArea}</span>
							</div>
						</PolygonInfo>
					</SectionContent>
				</SidebarSection>
			)}
	</>
);

export default memo(WorkflowDrawing);
